// All app data lives in this browser's localStorage.

export const NOTES_KEY = "notes";
export const FOLDERS_KEY = "folders";
export const PINNED_KEY = "pinnedNotes";

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    console.error(`Failed to save ${key}:`, e);
    return false;
  }
};

export const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Older versions kept signed-out notes under "localNotes" and pins per user
export const loadNotes = () => {
  const notes = readJSON(NOTES_KEY, null) ?? readJSON("localNotes", []);
  return Array.isArray(notes) ? notes : [];
};
export const saveNotes = (notes) => writeJSON(NOTES_KEY, notes);

export const loadFolders = () => {
  const folders = readJSON(FOLDERS_KEY, []);
  return Array.isArray(folders) ? folders : [];
};
export const saveFolders = (folders) => writeJSON(FOLDERS_KEY, folders);

export const loadPinnedIds = () => {
  const saved = readJSON(PINNED_KEY, null);
  if (Array.isArray(saved)) return saved;
  // First run: carry over pins from older versions
  const legacy = readJSON("pinnedNotes:guest", []);
  const fromNotes = loadNotes()
    .filter((n) => n.isPinned && !n.deletedAt)
    .map((n) => String(n.id));
  return Array.from(new Set([...(Array.isArray(legacy) ? legacy : []), ...fromNotes]));
};
export const savePinnedIds = (ids) => writeJSON(PINNED_KEY, ids);

const toTimestamp = (v, fallback) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const parsed = typeof v === "string" ? Date.parse(v) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Accepts a backup file ({ notes, folders }) or a plain array of notes
export const parseBackup = (data) => {
  const rawNotes = Array.isArray(data) ? data : data?.notes;
  const rawFolders = Array.isArray(data?.folders) ? data.folders : [];
  if (!Array.isArray(rawNotes)) throw new Error("No notes found in file");

  const now = Date.now();
  const notes = rawNotes
    .filter((n) => n && typeof n === "object")
    .map((n) => {
      const createdAt = toTimestamp(n.createdAt, now);
      return {
        ...n,
        id: n.id != null ? String(n.id) : newId(),
        title: typeof n.title === "string" ? n.title : "",
        content: typeof n.content === "string" ? n.content : "",
        createdAt,
        lastModified: toTimestamp(n.lastModified, createdAt),
        deletedAt: n.deletedAt ? toTimestamp(n.deletedAt, now) : null,
        folderId: n.folderId != null ? String(n.folderId) : null,
      };
    });
  const folders = rawFolders
    .filter((f) => f && f.id != null && typeof f.name === "string")
    .map((f) => ({
      ...f,
      id: String(f.id),
      parentId: f.parentId != null ? String(f.parentId) : null,
    }));
  return { notes, folders };
};

// Merge imported items, replacing any with the same id
export const mergeById = (existing, incoming) => {
  const map = new Map(existing.map((item) => [String(item.id), item]));
  incoming.forEach((item) => map.set(String(item.id), item));
  return Array.from(map.values());
};
